import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  formatTeacherBusyHours,
  formatTeacherBusyHoursChip,
  teacherBusyPercent,
} from '../utils/teacherBusyHours';

function slotChip(slot) {
  if (slot.status === 'free') {
    return { label: 'Free', color: 'success' };
  }
  if (slot.status === 'busy') {
    return { label: slot.busyLabel || 'Already has a class', color: 'error' };
  }
  return { label: 'Outside working hours', color: 'default' };
}

function teacherMatchesQuery(teacher, query) {
  if (!query) return true;
  const hay = `${teacher.name || ''} ${teacher.email || ''}`.toLowerCase();
  return hay.includes(query);
}

function confirmPartialAssign(teacher) {
  const misses = (teacher.slotMatches || []).filter((s) => s.status !== 'free');
  if (!misses.length) return true;
  const lines = misses
    .map((m) => `• ${m.label} — ${m.busyLabel || (m.status === 'busy' ? 'already has a class' : 'outside working hours')}`)
    .join('\n');
  return window.confirm(
    `${teacher.name} is not free for all times:\n${lines}\n\nAssign anyway?`
  );
}

function TeacherMatchRow({ teacher, selected, onSelect, variant = 'list' }) {
  const [openSlots, setOpenSlots] = useState(false);
  const slotCount = teacher.slotCount || (teacher.slotMatches || []).length || 0;
  const matchCount = teacher.matchCount ?? (teacher.slotMatches || []).filter((s) => s.status === 'free').length;
  const summary =
    slotCount > 0 ? `${matchCount}/${slotCount} times` : teacher.reasonLabel || '';
  const busyChip = formatTeacherBusyHoursChip(teacher);
  const busyFull = formatTeacherBusyHours(teacher);
  const busyPercent = teacherBusyPercent(teacher);
  const isCard = variant === 'cards';

  return (
    <Box
      sx={
        isCard
          ? {
              height: '100%',
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              borderRadius: 1,
              bgcolor: selected ? 'action.selected' : 'background.paper',
              overflow: 'hidden',
            }
          : { borderBottom: '1px solid', borderColor: 'divider' }
      }
    >
      <ListItemButton
        selected={selected}
        onClick={() => onSelect(teacher)}
        alignItems="flex-start"
        sx={{ py: 1.25, height: isCard ? 'auto' : undefined }}
      >
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={selected ? 600 : 500}>
                {teacher.name}
              </Typography>
              {teacher.isBatchShadow ? <Chip size="small" label="Shadow (this batch)" color="info" variant="outlined" /> : null}
              {summary ? <Chip size="small" label={summary} variant="outlined" /> : null}
              {busyChip ? (
                <Tooltip title={busyFull || busyChip}>
                  <Chip size="small" label={busyChip} variant="outlined" />
                </Tooltip>
              ) : null}
            </Stack>
          }
          primaryTypographyProps={{ component: 'div' }}
          secondaryTypographyProps={{ component: 'div' }}
          secondary={
            <Stack spacing={0.5} sx={{ mt: 0.25 }}>
              {busyPercent != null ? (
                <LinearProgress
                  variant="determinate"
                  value={busyPercent}
                  color={busyPercent >= 85 ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 1, maxWidth: isCard ? '100%' : 180 }}
                />
              ) : null}
              <Typography variant="caption" color="text.secondary">
                {busyFull || (teacher.reasonLabel && teacher.reason ? teacher.reasonLabel : teacher.email) || null}
              </Typography>
            </Stack>
          }
        />
      </ListItemButton>
      {(teacher.slotMatches || []).length > 0 ? (
        <Box sx={{ px: 2, pb: 1 }}>
          <Button size="small" onClick={() => setOpenSlots((v) => !v)} sx={{ minWidth: 0, px: 0 }}>
            {openSlots ? 'Hide times' : 'Show times'}
          </Button>
          <Collapse in={openSlots}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {(teacher.slotMatches || []).map((slot) => {
                const chip = slotChip(slot);
                return (
                  <Chip
                    key={`${slot.day_of_week}-${slot.start_time}-${slot.end_time}`}
                    size="small"
                    color={chip.color}
                    variant="outlined"
                    label={`${slot.label}: ${chip.label}`}
                  />
                );
              })}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

function TeacherSection({ teachers, value, onPick, emptyText, variant = 'list' }) {
  if (!teachers.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
        {emptyText}
      </Typography>
    );
  }
  if (variant === 'cards') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.25,
          p: 1.25,
        }}
      >
        {teachers.map((teacher) => (
          <TeacherMatchRow
            key={String(teacher.id)}
            teacher={teacher}
            selected={String(teacher.id) === String(value || '')}
            onSelect={onPick}
            variant="cards"
          />
        ))}
      </Box>
    );
  }
  return (
    <List disablePadding dense>
      {teachers.map((teacher) => (
        <TeacherMatchRow
          key={String(teacher.id)}
          teacher={teacher}
          selected={String(teacher.id) === String(value || '')}
          onSelect={onPick}
        />
      ))}
    </List>
  );
}

export default function BatchScheduleTeacherResults({
  exact = [],
  partial = [],
  unavailable = [],
  loading = false,
  value,
  onChange,
  allowEmpty = true,
  emptyLabel = 'No teacher yet — assign later',
  excludeIds = [],
  emptyMessage = 'No teachers found for the configured time slots.',
  variant = 'list',
  confirmIfIncomplete = true,
  exactTitle = 'Free for all times',
  partialTitle = 'Free for some times',
  unavailableTitle = 'Not free',
  emptyExactText = 'No one is free for every time yet.',
  showSelectionWarnings = true,
}) {
  const [query, setQuery] = useState('');
  const [showPartial, setShowPartial] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const q = query.trim().toLowerCase();
  const isCards = variant === 'cards';
  const sectionMaxHeight = isCards ? 360 : 240;
  const unavailableMaxHeight = isCards ? 320 : 180;

  const excludeSet = useMemo(
    () => new Set((excludeIds || []).filter(Boolean).map((id) => String(id))),
    [excludeIds]
  );

  const exactList = useMemo(
    () => (exact || []).filter((t) => !excludeSet.has(String(t.id)) && teacherMatchesQuery(t, q)),
    [exact, excludeSet, q]
  );
  const partialList = useMemo(
    () => (partial || []).filter((t) => !excludeSet.has(String(t.id)) && teacherMatchesQuery(t, q)),
    [partial, excludeSet, q]
  );
  const unavailableList = useMemo(
    () =>
      (unavailable || []).filter((t) => !excludeSet.has(String(t.id)) && teacherMatchesQuery(t, q)),
    [unavailable, excludeSet, q]
  );

  const selectedIsPartial = partial.some((t) => String(t.id) === String(value || ''));
  const selectedIsUnavailable = unavailable.some((t) => String(t.id) === String(value || ''));

  const partialOpen = showPartial || selectedIsPartial || (exactList.length === 0 && partialList.length > 0);
  const unavailableOpen =
    showUnavailable || selectedIsUnavailable || (exactList.length === 0 && partialList.length === 0);

  const pickTeacher = (teacher) => {
    if (!teacher) {
      onChange('');
      return;
    }
    if (String(teacher.id) === String(value || '')) return;
    if (confirmIfIncomplete && (teacher.matchCount ?? 0) < (teacher.slotCount || 0)) {
      if (!confirmPartialAssign(teacher)) return;
    }
    onChange(String(teacher.id));
  };

  const hasAny = exactList.length + partialList.length + unavailableList.length > 0;
  if (loading && !hasAny && !value) {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
        <CircularProgress size={22} />
        <Typography variant="body2">Matching teachers to these times…</Typography>
      </Box>
    );
  }

  if (!loading && !hasAny && !allowEmpty) {
    return <Alert severity="warning">{emptyMessage}</Alert>;
  }

  return (
    <Stack spacing={1.25}>
      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={22} />
          <Typography variant="body2">Matching teachers to these times…</Typography>
        </Box>
      ) : null}
      <TextField
        size="small"
        label="Search teachers"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Name or email"
      />

      {allowEmpty ? (
        <Button
          size="small"
          variant={!value ? 'contained' : 'outlined'}
          onClick={() => onChange('')}
          sx={{ alignSelf: 'flex-start' }}
        >
          {emptyLabel}
        </Button>
      ) : null}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2">{exactTitle} ({exactList.length})</Typography>
        </Box>
        <Box sx={{ maxHeight: sectionMaxHeight, overflow: 'auto' }}>
          <TeacherSection
            teachers={exactList}
            value={value}
            onPick={pickTeacher}
            emptyText={emptyExactText}
            variant={variant}
          />
        </Box>
      </Box>

      {partial.length > 0 ? (
        <Box>
          {!partialOpen ? (
            <Button size="small" onClick={() => setShowPartial(true)}>
              {partialList.length} more {partialTitle.toLowerCase()}
            </Button>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">{partialTitle} ({partialList.length})</Typography>
                {exactList.length > 0 && !selectedIsPartial ? (
                  <Button size="small" onClick={() => setShowPartial(false)}>
                    Hide
                  </Button>
                ) : null}
              </Box>
              <Box sx={{ maxHeight: sectionMaxHeight, overflow: 'auto' }}>
                <TeacherSection
                  teachers={partialList}
                  value={value}
                  onPick={pickTeacher}
                  emptyText="No partial matches."
                  variant={variant}
                />
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      {unavailable.length > 0 ? (
        <Box>
          {!unavailableOpen ? (
            <Button size="small" color="inherit" onClick={() => setShowUnavailable(true)}>
              Show teachers who are {unavailableTitle.toLowerCase()} ({unavailableList.length})
            </Button>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">{unavailableTitle} ({unavailableList.length})</Typography>
                {!selectedIsUnavailable ? (
                  <Button size="small" onClick={() => setShowUnavailable(false)}>
                    Hide
                  </Button>
                ) : null}
              </Box>
              <Box sx={{ maxHeight: unavailableMaxHeight, overflow: 'auto' }}>
                <TeacherSection
                  teachers={unavailableList}
                  value={value}
                  onPick={pickTeacher}
                  emptyText="Everyone is free for at least one time."
                  variant={variant}
                />
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      {showSelectionWarnings && selectedIsPartial ? (
        <Alert severity="warning">
          This teacher is not free for every time. You can still assign them.
        </Alert>
      ) : null}
      {showSelectionWarnings && selectedIsUnavailable ? (
        <Alert severity="warning">
          This teacher is not free for these times. You can still assign them.
        </Alert>
      ) : null}
    </Stack>
  );
}
